// The card an embed shows INSTEAD of itself when its data belongs to accounts
// and the visitor has none in force.
//
// The server is right to refuse (`website._guard_row_security`): an anonymous
// visitor on an object whose rows belong to individual ACCOUNTS gets
// 401 SIGN_IN_REQUIRED, a session that did not hold up gets 401
// SESSION_EXPIRED, and a signed-in caller holding no account of THIS
// application gets 403 PROJECT_USER_REQUIRED. What was missing was a client
// that turns that answer into the obvious next step instead of a dead,
// empty embed. This module is that client, ONCE, for every surface:
//
//  · `signInRequiredOf(err)` — recognise the refusal on any axios-shaped error
//    (one list of codes, `SIGN_IN_CODES`);
//  · `<SignInPromptCard>` — the card, its wording overridable per embed block
//    (`EmbedBlock.signInPrompt`);
//  · navigation is the HOST's: a `SignInPromptContext` adapter when a host
//    provides one, else the `AuthMenuContext` adapter the site viewer and the
//    downloaded runtime already provide for their `auth_menu` blocks — so the
//    card goes exactly where the header's own "Sign in" button goes
//    (`goToLogin(slug)` → `/login?next=<slug>` on the site viewer), and comes
//    back to this page signed in.
import { createContext, useContext, useState, type CSSProperties } from 'react'
import { useAuthMenu } from './renderers'

/** Server refusal codes that mean "this needs a (different) signed-in
 *  account" rather than "this is broken". */
export const SIGN_IN_CODES = ['SIGN_IN_REQUIRED', 'SESSION_EXPIRED', 'PROJECT_USER_REQUIRED'] as const
export type SignInCode = typeof SIGN_IN_CODES[number]

export interface SignInRequired {
  code: SignInCode
  /** The server's own wording, shown when the author set no body. */
  message: string
}

/** The author-overridable wording (`EmbedBlock.signInPrompt`). */
export interface SignInPromptCopy {
  title?: string
  body?: string
  signInLabel?: string
  registerLabel?: string
}

/** A host's sign-in navigation. `register` asks for the create-account form
 *  where the host has one. `code` lets the host end a session that is in
 *  force but useless here (SESSION_EXPIRED / PROJECT_USER_REQUIRED) before
 *  showing the form. */
export interface SignInPromptAdapter {
  signIn: (opts: { register?: boolean; code: SignInCode }) => void | Promise<void>
  /** Whether a "Create account" button makes sense on this host. */
  canRegister?: boolean
}

export const SignInPromptContext = createContext<SignInPromptAdapter | null>(null)

/** The refusal carried by an axios-shaped error, or null when it is any other
 *  failure. Reads both envelopes the platform uses: the top-level
 *  `{error:{code,message}}` and FastAPI's `{detail:{error:{…}}}`. */
export function signInRequiredOf(err: unknown): SignInRequired | null {
  const resp = (err as { response?: { status?: number; data?: unknown } } | null)?.response
  if (!resp || (resp.status !== 401 && resp.status !== 403)) return null
  const data = (resp.data ?? {}) as {
    error?: { code?: string; message?: string }
    detail?: { error?: { code?: string; message?: string } }
  }
  const envelope = data.error ?? data.detail?.error
  const code = envelope?.code
  if (!code || !(SIGN_IN_CODES as readonly string[]).includes(code)) return null
  return { code: code as SignInCode, message: String(envelope?.message || '') }
}

// Defaults per code. An author's `signInPrompt` replaces any of these.
const DEFAULT_COPY: Record<SignInCode, Required<Pick<SignInPromptCopy, 'title' | 'body'>>> = {
  SIGN_IN_REQUIRED: {
    title: 'Sign in to see your account',
    body: 'This shows records that belong to your account. Sign in, or create an account, to see yours.',
  },
  SESSION_EXPIRED: {
    title: 'Please sign in again',
    body: 'Your session has expired. Sign in again to continue.',
  },
  PROJECT_USER_REQUIRED: {
    title: 'Sign in with your account here',
    body: 'These records belong to individual accounts of this site. Sign in with your account for it to see yours.',
  },
}

const cardStyle: CSSProperties = {
  border: '1px solid var(--pg-border, #e2e8f0)',
  borderRadius: 'var(--pg-radius, 12px)',
  background: 'var(--pg-surface, #fff)',
  color: 'var(--pg-text, #0f172a)',
  padding: '24px 20px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  maxWidth: 520,
  margin: '0 auto',
}

const btnBase: CSSProperties = {
  padding: '8px 18px',
  borderRadius: 'var(--pg-button-radius, 8px)',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  border: '1px solid var(--pg-primary, #2563eb)',
}

/** The card. `prompt` is the embed block's own wording; anything it leaves
 *  out falls back to the defaults for `refusal.code`. */
export function SignInPromptCard({ refusal, prompt }: {
  refusal: SignInRequired
  prompt?: SignInPromptCopy
}) {
  const host = useContext(SignInPromptContext)
  const authMenu = useAuthMenu()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const defaults = DEFAULT_COPY[refusal.code]
  const title = prompt?.title || defaults.title
  // The author's body wins; else, for a refusal whose reason is specific to
  // this caller (a session in force but not usable here), the server's own
  // words; else the default.
  const body = prompt?.body
    || (refusal.code === 'PROJECT_USER_REQUIRED' && refusal.message) || defaults.body

  // A session that is in force but useless here must end before the sign-in
  // form can be shown — the host's sign-in page forwards a signed-in visitor
  // straight back, which would loop.
  const endsSession = refusal.code !== 'SIGN_IN_REQUIRED'

  const go = async (register: boolean) => {
    setBusy(true)
    setError('')
    try {
      if (host) {
        await host.signIn({ register, code: refusal.code })
      } else if (authMenu) {
        if (endsSession && authMenu.status === 'authenticated') {
          await authMenu.onLogout()
        }
        if (register && authMenu.onSignup) authMenu.onSignup()
        else authMenu.onLogin()
      }
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Could not open the sign-in page.')
    } finally {
      setBusy(false)
    }
  }

  const canSignIn = !!host || !!authMenu
  const canRegister = refusal.code === 'SIGN_IN_REQUIRED'
    && (host ? !!host.canRegister : !!authMenu?.onSignup)

  return (
    <div role="region" aria-label={title} data-signin-prompt={refusal.code} style={cardStyle}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true"
           stroke="var(--pg-primary, #2563eb)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="10" width="16" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </svg>
      <div style={{ fontWeight: 700, fontSize: 17 }}>{title}</div>
      <div style={{ fontSize: 14, color: 'var(--pg-text-secondary, #475569)', lineHeight: 1.5 }}>{body}</div>
      {canSignIn ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 6 }}>
          <button type="button" disabled={busy} onClick={() => void go(false)}
                  style={{ ...btnBase, background: 'var(--pg-primary, #2563eb)', color: 'var(--pg-on-primary, #fff)' }}>
            {prompt?.signInLabel || 'Sign in'}
          </button>
          {canRegister && (
            <button type="button" disabled={busy} onClick={() => void go(true)}
                    style={{ ...btnBase, background: 'transparent', color: 'var(--pg-primary, #2563eb)' }}>
              {prompt?.registerLabel || 'Create account'}
            </button>
          )}
        </div>
      ) : (
        // A host with no sign-in navigation (a designer canvas): say what
        // happens for a real visitor instead of offering a button that cannot
        // go anywhere.
        <div style={{ fontSize: 12.5, color: 'var(--pg-text-secondary, #64748b)' }}>
          Visitors are offered a sign-in here.
        </div>
      )}
      {error && <div role="alert" style={{ fontSize: 13, color: 'var(--pg-error, #dc2626)' }}>{error}</div>}
    </div>
  )
}
